import { createQuery, QueryableResults } from "@nova/queryable"
import {
    useBatchPilotTrackingQuery,
    usePilotTrackingQuery,
} from "features/pilot-tracking/PilotTrackingQueries"
import { useParams } from "react-router"
import { useMemo, useState } from "react"
import { Card } from "features/pilot-tracking/MetricLayout"
import { AircraftDailyUseCalendar } from "features/pilot-tracking/aircraft/AircraftDailyUseCalendar"
import { MissionsTable } from "features/pilot-tracking/flights-page/MissionsTable"
import { ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { PipChart } from "@nova/component-charts"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { AircraftFilesTable } from "features/pilot-tracking/aircraft-files/AircraftFilesTable"
import { CardCollection, StatCard } from "features/pilot-tracking/CardCollection"
import { AircraftImage } from "@nova/component-aircraft-images"
import { AircraftColorTag } from "features/pilot-tracking/aircraft/AircraftColorTag"
import { getGenericWorkspacePageWithoutHeader } from "../WorkspaceRoutes"
import { AircraftPageMaintenanceTab } from "../../../features/pilot-tracking/aircraft/AircraftPageMaintenanceTab"
import { getDisplayableAircraftData, zIndex } from "@nova/constants"
import { formatDistance } from "@nova/format"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useRouter } from "pages"
import { LinkText } from "components/LinkText"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { InputOutlined } from "@nova/ds-component-input"
import classNames from "classnames"
import {
    aircraftQuery,
    useAircraft,
    useUpdateAircraft,
} from "features/pilot-tracking/aircraft/AircraftQueries"
import { useNonDefaultPilotTrackingNestedSidebar } from "panels/pilot-tracking/NestedSidebars"
import {
    ContextMenu,
    ContextMenuItem,
} from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import { useIsVisible, useMergeRefs, useObserveElementRect } from "@nova/toolkit-pure-hooks"
import {
    AircraftPageHeader,
    AircraftPageStickyHeader,
} from "features/pilot-tracking/aircraft/AircraftPageHeaders"
import { selectTab } from "features/pilot-tracking/aircraft/slice"
import { createPortal } from "react-dom"
import { BetaTag } from "features/BetaTag"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"
import { EditableNotes } from "features/pilot-tracking/missions/notes/NotesCard"

export const AircraftPage = getGenericWorkspacePageWithoutHeader({
    Content: AircraftPageContent,
    maxWidth: 1200,
})

const AircraftNestedSidebar = () => {
    const dispatch = useAppDispatch()
    const { aircraftId = "" } = useParams()
    const router = useRouter("/workspace/:workspaceId")
    const [search, setSearch] = useState("")
    const [sort, setSort] = useState<"asc" | "desc">("asc")
    const [sortField, setSortField] = useState<"name" | "manufacturer">("name")
    const { aircraft, isLoading } = useAircraft()

    const { top, left, height, ref } = useObserveElementRect()
    const sortContextMenu = useAppSelector(selectActiveContextMenu("AircraftNestedSidebarSort"))
    const contextMenuSectionHeaders = ["Sort by", "Order"]
    const contextMenuSections: ContextMenuItem[][] = useMemo(() => {
        const sections: ContextMenuItem[][] = []
        sections.push([
            {
                title: "Name",
                Icon: sortField === "name" ? Icons.Checkmark : undefined,
                onClick: (e) => {
                    e.stopPropagation()
                    setSortField("name")
                },
            },
            {
                title: "Manufacturer",
                Icon: sortField === "manufacturer" ? Icons.Checkmark : undefined,
                onClick: (e) => {
                    e.stopPropagation()
                    setSortField("manufacturer")
                },
            },
        ])
        sections.push([
            {
                title: "Ascending",
                Icon: sort === "asc" ? Icons.Checkmark : undefined,
                onClick: (e) => {
                    e.stopPropagation()
                    setSort("asc")
                },
            },
            {
                title: "Descending",
                Icon: sort === "desc" ? Icons.Checkmark : undefined,
                onClick: (e) => {
                    e.stopPropagation()
                    setSort("desc")
                },
            },
        ])
        return sections
    }, [sort, sortField])

    const filteredAircraft = useMemo(() => {
        const q = search.toLowerCase()
        const filtered = q
            ? aircraft.filter((a) => {
                  const { displayName, make } = getDisplayableAircraftData(a)
                  return (
                      displayName.toLocaleLowerCase().includes(q) || make?.toLowerCase().includes(q)
                  )
              })
            : aircraft
        return filtered.toSorted((a, b) => {
            const { displayName: adn, make: am } = getDisplayableAircraftData(a)
            const { displayName: bdn, make: bm } = getDisplayableAircraftData(b)
            const valA =
                sortField === "manufacturer" ? (am?.toLowerCase() ?? "") : adn.toLowerCase()
            const valB =
                sortField === "manufacturer" ? (bm?.toLowerCase() ?? "") : bdn.toLowerCase()
            return valA.localeCompare(valB) * (sort === "asc" ? 1 : -1)
        })
    }, [aircraft, search, sort, sortField])

    return (
        <div className="w-[240px] border-r border-r-gray-700 px-3 py-5 flex flex-col gap-2">
            <div className="flex items-center gap-3 py-2">
                <router.Link to={{ path: "/fleet/aircraft" }}>
                    <Icons.SmallIcon
                        Icon={Icons.ArrowLeft}
                        className="text-gray-300 hover:text-white transition-colors cursor-pointer"
                    />
                </router.Link>
                <div className="text-[1.375rem] telegraf">Drones</div>
                <BetaTag />
            </div>
            <div className="flex items-center gap-1">
                <div className="shrink min-w-0">
                    <InputOutlined
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        placeholder="Search"
                        leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} />}
                        size="sm"
                    />
                </div>
                <div ref={ref}>
                    <ButtonTertiary
                        LeftIcon={Icons.Sort}
                        className="!min-w-fit !px-2 !h-full"
                        onClick={() =>
                            dispatch(
                                setActiveContextMenu(
                                    sortContextMenu
                                        ? undefined
                                        : {
                                              type: "AircraftNestedSidebarSort",
                                              position: {
                                                  top: top + height + 4,
                                                  left,
                                              },
                                          }
                                )
                            )
                        }
                    />
                </div>
                {sortContextMenu && (
                    <ContextMenu
                        sections={contextMenuSections}
                        sectionHeaders={contextMenuSectionHeaders}
                        onBlur={() => dispatch(setActiveContextMenu(undefined))}
                        isFixedPosition={true}
                        top={sortContextMenu.position.top}
                        left={sortContextMenu.position.left}
                        keepOpenOnSelect
                        iconColor="#16C949" // green-500
                    />
                )}
            </div>
            {isLoading ? (
                <div className="grow flex justify-center items-center">
                    <div className="loader" />
                </div>
            ) : (
                <ScrollableArea className="grow">
                    <div className="flex flex-col gap-1">
                        {filteredAircraft.length ? (
                            filteredAircraft.map((a) => {
                                const { displayName, make } = getDisplayableAircraftData(a)
                                const isActive = aircraftId === a.id
                                return (
                                    <router.Link
                                        key={a.id}
                                        to={{
                                            path: "/fleet/aircraft/:aircraftId",
                                            params: { aircraftId: a.id },
                                        }}
                                    >
                                        <div
                                            className={classNames(
                                                "py-1.5 pr-1.5 pl-0 rounded-lg cursor-pointer flex gap-4 items-center transition-colors",
                                                {
                                                    "bg-white-200": isActive,
                                                    "hover:bg-white-100": !isActive,
                                                }
                                            )}
                                        >
                                            <div className="-ml-10 -my-1.5 h-[60px] w-[106px] rounded-r-md overflow-hidden shrink-0 relative">
                                                <AircraftImage
                                                    make={a.manufacturer}
                                                    model={a.model}
                                                    quality="half"
                                                    className="w-full h-full object-cover"
                                                />
                                                <div
                                                    className={classNames(
                                                        "absolute inset-0 bg-gradient-to-r from-[#121216] to-transparent pointer-events-none transition-opacity",
                                                        {
                                                            "opacity-0": isActive,
                                                        }
                                                    )}
                                                />
                                            </div>
                                            <div className="relative flex flex-col min-w-0">
                                                <AircraftColorTag
                                                    color={a.color}
                                                    className="absolute right-full inset-y-0 mr-2"
                                                />
                                                <small className="xs text-gray-400 truncate !normal-case">
                                                    {make}
                                                </small>
                                                <div className="truncate text-white">
                                                    {displayName}
                                                </div>
                                            </div>
                                        </div>
                                    </router.Link>
                                )
                            })
                        ) : (
                            <div className="flex flex-col gap-2 items-center justify-center py-8">
                                <div className="text-white">No Drones Found</div>
                                <div className="text-xs text-gray-100">
                                    Try a different keyword.
                                </div>
                            </div>
                        )}
                    </div>
                </ScrollableArea>
            )}
        </div>
    )
}

type AircraftQueryResult = QueryableResults<typeof aircraftQuery>

function AircraftPageContent() {
    useNonDefaultPilotTrackingNestedSidebar({ Sidebar: AircraftNestedSidebar })
    const tab = useAppSelector(selectTab)
    const { aircraftId = "" } = useParams()
    const aircraftQueryResults = useMemo(() => aircraftQuery(aircraftId), [aircraftId])
    const { data, isLoading } = usePilotTrackingQuery(aircraftQueryResults)

    const { ref: rectRef, width, left } = useObserveElementRect()
    const { containerRef: visibleContainerRef, isVisible: isMainHeaderVisible } = useIsVisible()
    const mergedRef = useMergeRefs(rectRef, visibleContainerRef)

    if (isLoading) {
        return (
            <div className="w-full h-full flex justify-center items-center">
                <div className="loader" />
            </div>
        )
    }
    if (!data?.[0]) return null

    const aircraft = data[0]

    return (
        <div className="flex flex-col h-full">
            <div className="shrink-0" ref={mergedRef}>
                <AircraftPageHeader aircraft={aircraft} />
            </div>
            {createPortal(
                <div
                    className="absolute top-2.5 -translate-y-px -translate-x-px pointer-events-none"
                    style={{
                        left,
                        width,
                        zIndex: zIndex.HighPriorityModal,
                    }}
                >
                    <AircraftPageStickyHeader aircraft={aircraft} visible={!isMainHeaderVisible} />
                </div>,
                document.getElementById("portal")!
            )}
            <div className="isolate h-full">
                {tab === "overview" && <OverviewTab aircraft={aircraft} />}
                {tab === "history" && <HistoryTab aircraftId={aircraft.id} />}
                {tab === "maintenance" && <AircraftPageMaintenanceTab />}
            </div>
        </div>
    )
}

const HistoryTab = ({ aircraftId }: { aircraftId: string }) => {
    return (
        <div className="flex flex-col gap-4 h-full pb-12">
            <MissionsTable.Filters filters={["date", "pilot", "search"]} />
            <MissionsTable
                columns={["started_at", "pilot", "flightTime", "distanceFlown"]}
                selectable={false}
                filters={{ aircraftId }}
                origin={{ page: "drone", droneId: aircraftId }}
            />
        </div>
    )
}

const OverviewTab = ({ aircraft }: { aircraft: AircraftQueryResult[number] }) => {
    const queries = useMemo(
        () => ({
            numFlights: createQuery({
                resource: "missions",
                aggregates: [{ field: "id", func: "COUNT", alias: "numFlights" }],
                filters: [{ field: "aircraft_id", op: "=", value: aircraft.id }],
            }),
            totalDistance: createQuery({
                resource: "metadata",
                aggregates: [{ field: "distance_flown", func: "SUM", alias: "totalDistance" }],
                filters: [{ field: "mission.aircraft_id", op: "=", value: aircraft.id }],
            }),
            totalFlightTime: createQuery({
                resource: "metadata",
                aggregates: [{ field: "airtime_hours", func: "SUM", alias: "totalFlightTime" }],
                filters: [{ field: "mission.aircraft_id", op: "=", value: aircraft.id }],
            }),
            avgFlightTime: createQuery({
                resource: "metadata",
                aggregates: [{ field: "airtime_hours", func: "AVG", alias: "avgFlightTime" }],
                filters: [{ field: "mission.aircraft_id", op: "=", value: aircraft.id }],
            }),
            aircraftsPilotHours: createQuery({
                resource: "metadata",
                select: ["mission.user_id"],
                aggregates: [
                    {
                        field: "airtime_hours",
                        func: "SUM",
                        alias: "aircraftsPilotHours",
                    },
                ],
                filters: [
                    {
                        field: "mission.aircraft_id",
                        op: "=",
                        value: aircraft.id,
                    },
                    { field: "mission.user_id", op: "IS NOT NULL", value: null },
                ],
                orderBy: [{ dir: "DESC", field: "aircraftsPilotHours" }],
            }),
            lastFlight: createQuery({
                resource: "metadata",
                aggregates: [{ field: "started_at", func: "MAX", alias: "lastFlightTime" }],
                filters: [{ field: "mission.aircraft_id", op: "=", value: aircraft.id }],
            }),
        }),
        [aircraft.id]
    )

    const { data } = useBatchPilotTrackingQuery(queries)
    const users = useAppSelector(selectAllRegisteredMembers)
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const { updateAircraft } = useUpdateAircraft()

    const numFlights = +(data?.numFlights[0]?.numFlights ?? 0)
    const totalDistanceMeters = +(data?.totalDistance[0]?.totalDistance ?? 0)
    const totalFlightTimeHours = +(data?.totalFlightTime[0]?.totalFlightTime ?? 0)
    const avgFlightTimeHours = +(data?.avgFlightTime[0]?.avgFlightTime ?? 0)

    const avgHours = Math.floor(avgFlightTimeHours)
    const avgMinutes = Math.round((avgFlightTimeHours % 1) * 60)
    const avgLabel =
        avgHours > 0
            ? `${avgHours} hour${avgHours !== 1 ? "s" : ""} ${avgMinutes} minutes`
            : `${avgMinutes} minutes`

    return (
        <div className="flex flex-col gap-8 pb-6">
            <div className="flex flex-col gap-4">
                <CardCollection
                    cards={[
                        <StatCard
                            key="total"
                            Icon={Icons.Drone}
                            title="Total Flights"
                            body={`${numFlights} flights`}
                        />,
                        <StatCard
                            key="distance"
                            Icon={Icons.Route}
                            title="Total Distance Flown"
                            body={formatDistance({
                                value: totalDistanceMeters,
                                unit: distanceUnit,
                            })}
                        />,
                        <StatCard
                            key="flight-time"
                            Icon={Icons.Recent}
                            title="Total Flight Time"
                            body={`${Math.round(totalFlightTimeHours)} hours`}
                        />,
                        <StatCard
                            key="avg-flight-time"
                            Icon={Icons.Recent}
                            title="Average flight time"
                            body={avgLabel}
                        />,
                    ]}
                />

                <div className="flex gap-5">
                    <AircraftDailyUseCalendar aircraftId={aircraft.id} />
                    <div className="flex-1">
                        <MostActivePilotsCard
                            pilotHours={data?.aircraftsPilotHours}
                            totalFlightTimeHours={totalFlightTimeHours}
                            users={users}
                        />
                    </div>
                </div>
            </div>
            <Card className="flex-1 flex px-10 py-9 gap-10">
                <div className="flex-1 min-w-0 flex flex-col gap-2">
                    <h2 className="text-gray-400 telegraf px-2">Notes</h2>
                    <EditableNotes
                        key={aircraft.id}
                        notes={aircraft.notes}
                        onSave={(notes) =>
                            updateAircraft({ aircraftId: aircraft.id, data: { notes } })
                        }
                        EmptyComponent={NoAircraftNotes}
                    />
                </div>
                <div className="grid grid-rows-3 grid-cols-2 gap-x-4 gap-y-5 self-start">
                    <Metadata title="Registration Number" data={aircraft.registration_number} />
                    <Metadata title="Serial Number" data={aircraft.serial_number} />
                    <Metadata
                        title="Warranty Expires"
                        data={aircraft.warranty_expires_at?.toDateString()}
                    />
                    <Metadata title="Purchase Date" data={aircraft.purchased_at?.toDateString()} />
                    <Metadata title="Firmware Version" data={undefined} />
                    <Metadata
                        title="Last Flight"
                        data={data?.lastFlight[0].lastFlightTime?.toDateString()}
                    />
                </div>
            </Card>
            <AircraftFilesTable aircraftId={aircraft.id} />
        </div>
    )
}

const NoAircraftNotes = ({ startEditing }: { startEditing: () => void }) => (
    <div className="flex-1 flex flex-col items-center justify-center">
        <h1 className="text-gray-0 telegraf">No Added Notes</h1>
        <h5 className="text-gray-100">Add notes to provide operational context.</h5>
        <ButtonTertiary
            title="Notes"
            LeftIcon={Icons.Plus}
            onClick={startEditing}
            className="mt-2"
        />
    </div>
)

const MostActivePilotsCard = ({
    pilotHours,
    totalFlightTimeHours,
    users,
}: {
    pilotHours:
        | Array<{ "mission.user_id": string | null; aircraftsPilotHours: number | null }>
        | undefined
    totalFlightTimeHours: number
    users: Array<{ id?: string; fullName?: string }> | undefined
}) => {
    const router = useRouter("/workspace/:workspaceId")

    return (
        <Card className="bg-white-100 px-10 py-9 h-full w-full">
            {pilotHours?.length ? (
                <div className="flex flex-col gap-8">
                    <h2 className="text-gray-50 telegraf">Most Active Pilots</h2>
                    {pilotHours?.map((aph) => {
                        const user = users?.find((u) => u.id === aph["mission.user_id"])
                        if (!user || !user.id || !aph.aircraftsPilotHours || !totalFlightTimeHours)
                            return null

                        return (
                            <div className="flex gap-5 items-center" key={aph["mission.user_id"]}>
                                <div className="w-16 h-16 rounded-xl overflow-hidden shrink-0">
                                    <router.Link
                                        to={{
                                            params: { pilotId: user.id },
                                            path: "/fleet/pilots/:pilotId",
                                        }}
                                    >
                                        <ProfilePhoto userId={user.id!} fullName={user.fullName!} />
                                    </router.Link>
                                </div>
                                <div className="flex flex-col gap-1 flex-1">
                                    <div className="flex justify-between">
                                        <div className="flex items-end gap-1">
                                            <router.Link
                                                to={{
                                                    params: { pilotId: user.id },
                                                    path: "/fleet/pilots/:pilotId",
                                                }}
                                            >
                                                <LinkText>
                                                    <h4>{user.fullName}</h4>
                                                </LinkText>
                                            </router.Link>
                                        </div>
                                        <small className="text-gray-100">
                                            {Number(aph.aircraftsPilotHours).toFixed(2)} HOURS
                                        </small>
                                    </div>
                                    <PipChart
                                        percentage={aph.aircraftsPilotHours / totalFlightTimeHours}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center h-full gap-2 ">
                    <h1 className="text-white telegraf">No Activity Recorded</h1>
                    <h5 className="text-gray-100">
                        No pilots have logged flights for this drone yet.
                    </h5>
                </div>
            )}
        </Card>
    )
}

const Metadata = ({ title, data }: { title: string; data: string | null | undefined }) => {
    return (
        <div className="flex flex-col">
            <h5 className="text-gray-400">{title}</h5>
            {data ? <h5 className="text-gray-50">{data}</h5> : <h5 className="text-gray-400">—</h5>}
        </div>
    )
}
